import React from 'react'
import { Avatar, Box, Chip, Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'

import ReactMarkdown from 'react-markdown'

import usePrimaryColor from '../../../lib/hooks/usePrimaryColor'
import AssistantAvatar from '../AssistantAvatar'

const streamingCursorSx = {
  '&::after': {
    content: '"▋"',
    display: 'inline',
    animation: 'blink 1s step-end infinite',
    '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } }
  }
}

export type AssistantMessageProps = {
  sender: 'user' | 'assistant'
  text: string
  timestamp?: Date
  children?: React.ReactNode
  streaming?: boolean
  skill?: string
}

const SKILL_CHIP_TEST_ID = 'assistant-message-skill-chip'

/**
 * Renders a single chat bubble with an avatar.
 * User messages are right-aligned with a person icon avatar;
 * assistant messages are left-aligned with the assistant avatar.
 * User bubbles use the institution's primaryColor; both avatars use a grey circle with a grey icon.
 */
const AssistantMessageBase = ({
  sender,
  text,
  timestamp,
  children,
  streaming,
  skill
}: AssistantMessageProps) => {
  const color = usePrimaryColor()
  const isUser = sender === 'user'

  const avatar = isUser ? (
    <Avatar sx={{ width: 32, height: 32, backgroundColor: 'grey.300', flexShrink: 0 }}>
      <PersonIcon sx={{ fontSize: 18, color: 'grey.700' }} />
    </Avatar>
  ) : (
    <AssistantAvatar />
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1
      }}
    >
      {avatar}
      <Box
        sx={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start'
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderRadius: isUser ? '8px 0px 8px 8px' : '0px 8px 8px 8px',
            backgroundColor: isUser ? color : '#fff',
            border: isUser ? 'none' : '1px solid',
            borderColor: isUser ? 'transparent' : 'grey.300',
            color: isUser ? '#fff' : 'text.primary',
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 1,
            alignItems: 'baseline'
          }}
        >
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            {isUser && skill && (
              <Chip
                label={skill}
                size="small"
                data-testid={SKILL_CHIP_TEST_ID}
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  mr: 0.5,
                  verticalAlign: 'middle',
                  // Sit on the colored user bubble: translucent white surface
                  // with white text so the chip reads against any primaryColor.
                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                  color: '#fff',
                  '& .MuiChip-label': { color: '#fff' }
                }}
              />
            )}
            {isUser ? (
              <Typography
                variant="body2"
                component={skill ? 'span' : 'p'}
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {text}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                component="div"
                data-testid={streaming ? 'streaming-cursor' : undefined}
                sx={[{ '& p': { my: 0 } }, streaming ? streamingCursorSx : false]}
              >
                {streaming ? text : <ReactMarkdown>{text}</ReactMarkdown>}
              </Typography>
            )}
            {children}
          </Box>
          {timestamp && (
            <Typography
              component="time"
              dateTime={timestamp.toISOString()}
              variant="caption"
              sx={{
                flex: '0 0 auto',
                ml: 'auto',
                color: isUser ? 'rgba(255,255,255,0.7)' : 'text.disabled'
              }}
            >
              {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const AssistantMessage = React.memo(AssistantMessageBase)

export default AssistantMessage
